"use client";
import { useMemo, useState } from "react";
import { Department, DepartmentEmployee, DepartmentStatusCode } from "../services/department-service";
import { ATTENDANCE_MODE, ATTENDANCE_MODE_LABEL } from "../../../constants/status";

interface DepartmentDetailModalProps {
  name: string;
  data: Department;
  loading: boolean;
  onClose: () => void;
}

/** Bỏ dấu + lowercase để tìm kiếm không dấu (gõ "duc" ra "Đức"). */
const normalize = (v: string) =>
  (v || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // bỏ dấu thanh/dấu mũ sau khi tách tổ hợp NFD
    .replace(/đ/g, "d");        // đ → d (không nằm trong dải tổ hợp trên)

const initialsOf = (fullName: string) => {
  const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts.length === 1
    ? parts[0].charAt(0).toUpperCase()
    : (parts[parts.length - 2].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const EmployeeAvatar: React.FC<{ user: DepartmentEmployee }> = ({ user }) => {
  const [broken, setBroken] = useState(false);
  if (user.avatar && !broken) {
    return (
      <img
        src={user.avatar}
        alt={user.name}
        onError={() => setBroken(true)}
        className="h-9 w-9 shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600">
      {initialsOf(user.name)}
    </div>
  );
};

const DepartmentDetailModal: React.FC<DepartmentDetailModalProps> = ({
  name,
  data,
  loading,
  onClose,
}) => {
  const [keyword, setKeyword] = useState("");

  const employees = useMemo(() => data.users ?? [], [data.users]);

  const filtered = useMemo(() => {
    const kw = normalize(keyword.trim());
    if (!kw) return employees;
    return employees.filter((u) =>
      [u.name, u.username, u.phone, u.email].some((f) => normalize(String(f || "")).includes(kw))
    );
  }, [employees, keyword]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-white p-8 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-slate-800">Chi tiết bộ phận: {name}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700">✕</button>
        </div>

        <div className="mt-5 flex-1 overflow-y-auto text-sm text-slate-700">
          {loading ? (
            <p>Đang tải...</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                <div><b>ID:</b> {data.id}</div>
                <div><b>Company ID:</b> {data.company_id}</div>
                {/* Đếm từ danh sách nhân sự thực tế, KHÔNG dùng cột `user_count` — cột đó là bộ đếm
                    tăng/giảm khi chuyển bộ phận nên dễ trôi so với thực tế. */}
                <div><b>Người dùng:</b> {employees.length}</div>
                <div><b>Bộ phận trực thuộc:</b> {data.parent_department_name || "—"}</div>
                <div className="col-span-2"><b>Mô tả:</b> {data.description || "—"}</div>
                <div><b>Trạng thái:</b> {data.status === DepartmentStatusCode.ACTIVE ? "Đang hoạt động" : "Tạm ngưng"}</div>
                <div>
                  <b>Chế độ chấm công mặc định:</b>{" "}
                  {ATTENDANCE_MODE_LABEL[data.default_attendance_mode ?? ATTENDANCE_MODE.SHIFT]}
                </div>
                <div><b>Tạo lúc:</b> {data.created_at}</div>
                <div><b>Cập nhật:</b> {data.updated_at}</div>
              </div>

              {/* Nhân sự thuộc bộ phận */}
              <div className="mt-6 border-t pt-5">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <h4 className="text-base font-semibold text-slate-800">
                    Nhân sự đang hoạt động
                    <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                      {employees.length}
                    </span>
                  </h4>
                  {employees.length > 0 && (
                    <input
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      placeholder="Tìm theo tên, tài khoản, SĐT, email…"
                      className="w-64 rounded-lg border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                    />
                  )}
                </div>

                {employees.length === 0 ? (
                  <p className="rounded-lg bg-slate-50 px-4 py-6 text-center italic text-slate-500">
                    Bộ phận này chưa có nhân sự nào đang hoạt động.
                  </p>
                ) : filtered.length === 0 ? (
                  <p className="rounded-lg bg-slate-50 px-4 py-6 text-center italic text-slate-500">
                    Không tìm thấy nhân sự khớp với "{keyword}".
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-lg border">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 text-xs uppercase text-slate-600">
                        <tr>
                          <th className="w-[60px] px-3 py-2 text-center">STT</th>
                          <th className="px-3 py-2 text-left">Nhân viên</th>
                          <th className="w-[130px] px-3 py-2 text-left">Số điện thoại</th>
                          <th className="px-3 py-2 text-left">Email</th>
                          <th className="w-[120px] px-3 py-2 text-left">Ngày vào làm</th>
                          <th className="w-[150px] px-3 py-2 text-left">Vai trò</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {filtered.map((u, idx) => (
                          <tr key={u.id} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                            <td className="px-3 py-2 text-center text-slate-600">{idx + 1}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <EmployeeAvatar user={u} />
                                <div className="min-w-0">
                                  <div className="truncate font-medium text-slate-800">{u.name || `#${u.id}`}</div>
                                  <div className="truncate text-xs text-slate-500">{u.username || "—"}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2">{u.phone || "—"}</td>
                            <td className="px-3 py-2">
                              <span className="block max-w-[220px] truncate">{u.email || "—"}</span>
                            </td>
                            <td className="px-3 py-2">{u.joining_date || "—"}</td>
                            <td className="px-3 py-2">
                              <div className="flex flex-wrap gap-1">
                                {Number(u.is_owner) === 1 && (
                                  <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">Chủ</span>
                                )}
                                {Number(u.is_driver) === 1 && (
                                  <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">Tài xế</span>
                                )}
                                {Number(u.is_all_branch) === 1 && (
                                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Toàn chi nhánh</span>
                                )}
                                {Number(u.is_owner) !== 1 && Number(u.is_driver) !== 1 && Number(u.is_all_branch) !== 1 && (
                                  <span className="text-xs text-slate-400">—</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="rounded-full border px-4 py-2">Đóng</button>
        </div>
      </div>
    </div>
  );
};

export default DepartmentDetailModal;
